// Companion/ChatContextPills.tsx
//
// What Ora can see, shown before the student sends rather than after (ORA-662).
// One pill for the item on screen — or for the page, when there is no item —
// plus one for any highlighted text, in the composer bar above the image chips.
//
// Each pill can be removed. Removing the page/content pill replaces it with a
// dashed ghost that puts it back, so the row never collapses and the student
// can undo; removing the highlight pill just drops the selection, which is
// one-shot anyway. Detach is sticky per item: `chatContextKey` decides when it
// lapses, and `useAppContextEnvelope` clears it the moment the key changes.

import {
  BookOpenText,
  Browser,
  FileText,
  ListBullets,
  PlayCircle,
  Plus,
  Quotes,
  Stack,
  X,
} from '@phosphor-icons/react';
import { useSelector } from 'react-redux';

import {
  clearHighlightedChatSelection,
  isSameChatAnchor,
  setDetachedChatContextKey,
  useChatAppContextInputs,
} from '@/chatContext/chatAppContextStore';
import { useChatBlockThreadState } from '@/chatContext/chatBlockThread';
import { chatContextKey } from '@/chatContext/chatContextKey';
import { Badge } from '@/components/ui/badge';
import Tooltip from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  ChatAnchorIcon,
  ChatAnchorInput,
  ChatAnchorType,
  ChatRouteId,
  ChatSurface,
} from '@/types/chatEnvelope';
import { capture } from '@/utils/analytics';
import { lectureNodeKindSpec } from '@/components/ora/Home/LectureTree/lectureNodeKinds';
import type { RootState } from '@/store/store';
import ChatBlockScopePill from './ChatBlockScopePill';
import { CONTEXT_ICON } from './ChatContextLabel';
import { useChatDocumentViewer } from '@/hooks/useChatDocumentViewer';
import { useMcGrawHillSourceLabel } from '../../../../mcgrawHill/mcgrawHillChapter'; // mcgraw-hill
import {
  useChatDocumentViewerStatus,
  type ChatDocumentViewerStatus,
} from '@/providers/chatDocumentViewerStatus';

interface ChatContextPillsProps {
  /** The named route the surface's envelope resolved to. */
  route: ChatRouteId;
  /** Which shell the composer is in. The chat page shows no page pill. */
  surface: ChatSurface;
}

/** The noun each kind is called in the ghost tooltip and the placeholder. */
const ANCHOR_NOUN: Record<ChatAnchorType, string> = {
  question: 'question',
  flashcard: 'flashcard',
  video: 'video',
  article: 'article',
  lecture: 'lecture',
  exam: 'exam',
};

/**
 * The icon a publisher asks for over its anchor type's own: the content-type
 * icons the rest of the app draws (the nav, the topic cards' count chips).
 */
const ANCHOR_ICON_OVERRIDE: Record<ChatAnchorIcon, typeof Browser> = {
  reading: BookOpenText,
  video: PlayCircle,
  question: ListBullets,
  flashcard: Stack,
};

/** The fallback pill label for a publisher that sent none. */
const ANCHOR_FALLBACK_LABEL: Record<ChatAnchorType, string> = {
  question: 'This question',
  flashcard: 'This flashcard',
  video: 'This video',
  article: 'This article',
  lecture: 'This lecture',
  exam: 'This exam',
};

/**
 * What each page is called. Anything not named here — the study routes, which
 * normally carry an anchor of their own — falls back to "This page".
 */
const PAGE_LABEL: Partial<Record<ChatRouteId, string>> = {
  dashboard: 'Dashboard',
  performance: 'Performance',
  'study.summary': 'Study summary',
  'qbank.start': 'QBank',
  'self-assessments': 'Self-assessments',
  topics: 'Topics',
  library: 'Library',
  videos: 'Videos',
  'flashcard-decks': 'Flashcard decks',
  games: 'Games',
  help: 'Help',
  account: 'Account',
  webinar: 'Webinar',
};

/** Highlighted text is quoted down to a glance. */
const SELECTION_PREVIEW_MAX_CHARS = 28;

const selectionPillLabel = (selection: string): string => {
  const text = selection.replace(/\s+/g, ' ').trim();

  const preview =
    text.length > SELECTION_PREVIEW_MAX_CHARS
      ? `${text.slice(0, SELECTION_PREVIEW_MAX_CHARS).trimEnd()}…`
      : text;

  return `Highlighted: “${preview}”`;
};

/** A file name is cut down to this many characters on its pill. */
const DOCUMENT_NAME_MAX_CHARS = 28;

/**
 * The open side viewer's pill (ORA-736): the file's name without its
 * extension, cut to a glance, and the page on the viewer's indicator.
 */
export const documentPillLabel = ({
  fileName,
  page,
}: ChatDocumentViewerStatus): string | null => {
  if (!fileName) return null;

  const name = fileName.replace(/\.[a-z0-9]{1,5}$/i, '').trim() || fileName;

  const short =
    name.length > DOCUMENT_NAME_MAX_CHARS
      ? `${name.slice(0, DOCUMENT_NAME_MAX_CHARS).trimEnd()}…`
      : name;

  return page ? `${short} · p. ${page}` : short;
};

/**
 * True where the page/content pill is shown at all. The chat page has no page
 * behind it.
 */
const hasPageContext = (route: ChatRouteId, surface: ChatSurface): boolean =>
  route !== 'chat' && surface !== 'chat_page';

/**
 * The composer's placeholder, which follows the pill: what is attached, or
 * that nothing is. `undefined` leaves the composer's own default in place.
 */
export const chatContextPlaceholder = ({
  anchorType,
  anchorNoun,
  route,
  surface,
  isDetached,
}: {
  anchorType: ChatAnchorType | null;
  /** Overrides the anchor type's own noun (a lecture that is really a case). */
  anchorNoun?: string;
  route: ChatRouteId;
  surface: ChatSurface;
  isDetached: boolean;
}): string | undefined => {
  if (!hasPageContext(route, surface)) return undefined;
  if (isDetached) return 'Ask Ora anything…';
  if (anchorType) {
    return `Ask about this ${anchorNoun ?? ANCHOR_NOUN[anchorType]}…`;
  }

  return 'Ask about this page…';
};

/**
 * The composer's placeholder for the anchor on screen. A lecture anchor takes
 * its tree node's noun when its kind has one (a Case is "this case").
 */
export const useChatContextPlaceholder = ({
  anchor,
  route,
  surface,
  isDetached,
}: {
  anchor: ChatAnchorInput | null;
  route: ChatRouteId;
  surface: ChatSurface;
  isDetached: boolean;
}): string | undefined => {
  const lectureId = anchor?.type === 'lecture' ? anchor.lectureId : undefined;

  const anchorNoun = useSelector((state: Partial<RootState>) =>
    lectureId
      ? lectureNodeKindSpec(state.lectureTree?.nodesById[lectureId]?.kind)
          ?.chatNoun
      : undefined
  );

  return chatContextPlaceholder({
    anchorType: anchor?.type ?? null,
    anchorNoun,
    route,
    surface,
    isDetached,
  });
};

interface PillProps {
  label: string;
  icon: typeof Browser;
  /** Omitted for a pill something else takes away (the open document's). */
  onRemove?: () => void;
  removeLabel?: string;
  testId: string;
  onActivate?: () => void;
  /** Which icon is drawn, for tests; the icon itself is `icon`. */
  iconName?: string;
}

/**
 * An attached pill: what Ora can see, with the X that takes it away. A pill
 * that opens something (a lecture, beside the chat) holds that action and the
 * X as sibling buttons, so each keeps its own keyboard activation.
 */
const ContextPill = ({
  label,
  icon: Icon,
  onRemove,
  removeLabel,
  testId,
  onActivate,
  iconName,
}: PillProps) => (
  <Tooltip title="Ora can see this." placement="top">
    {/* The badge is not a ref-forwarding component, so the trigger is the
        wrapper rather than the badge itself. */}
    <span className="inline-flex min-w-0 max-w-full">
      <Badge
        variant="chip-highlight"
        className="group relative min-w-0 max-w-full"
        data-testid={testId}
        data-icon={iconName}
      >
        {onActivate ? (
          <button
            type="button"
            onClick={onActivate}
            aria-label={`Open ${label}`}
            className="inline-flex min-w-0 cursor-pointer items-center gap-1 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-default"
          >
            <Icon size={12} aria-hidden="true" />
            <span className="min-w-0 truncate">{label}</span>
          </button>
        ) : (
          <>
            <Icon size={12} aria-hidden="true" />
            <span className="min-w-0 truncate">{label}</span>
          </>
        )}
        {onRemove ? (
          <button
            type="button"
            aria-label={removeLabel ?? `Remove ${label} from this chat`}
            onClick={onRemove}
            className={cn(
              // The pill sizes to its label; the X fades in *over* the label's
              // tail rather than holding a blank slot beside it (ORA-664). It
              // sits on a run of the chip's own colour so it reads over text.
              'absolute inset-y-0 right-0 flex items-center justify-end rounded-r-full pl-4 pr-1.5',
              'bg-gradient-to-r from-transparent to-surface-highlight to-55% dark:to-secondary',
              'text-secondary-foreground transition-opacity hover:text-fg-primary',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-default',
              // Invisible means untouchable too: it lies over the label now, and
              // a tap that lands there must read as a tap on the pill.
              'pointer-events-none opacity-0',
              'focus-visible:pointer-events-auto focus-visible:opacity-100',
              'group-focus-within:pointer-events-auto group-focus-within:opacity-100',
              'group-hover:pointer-events-auto group-hover:opacity-100',
              // No hover to reveal it with: the X stays in the row, beside the
              // label, at a size a thumb can hit.
              '[@media(hover:none)]:pointer-events-auto [@media(hover:none)]:static [@media(hover:none)]:size-6 [@media(hover:none)]:shrink-0 [@media(hover:none)]:justify-center [@media(hover:none)]:bg-none [@media(hover:none)]:p-0 [@media(hover:none)]:opacity-100'
            )}
          >
            <X size={12} aria-hidden="true" />
          </button>
        ) : null}
      </Badge>
    </span>
  </Tooltip>
);

/** The slot the removed pill leaves behind, which puts it back. */
const GhostPill = ({
  label,
  noun,
  onRestore,
}: {
  label: string;
  noun: string;
  onRestore: () => void;
}) => (
  <Tooltip title={`Add this ${noun} back`} placement="top">
    <button
      type="button"
      onClick={onRestore}
      data-testid="chat-context-pill-ghost"
      className={cn(
        'inline-flex min-w-0 max-w-full items-center gap-1 rounded-full',
        'border border-dashed border-border-subtle px-2 py-0.5',
        'text-xs font-medium text-fg-secondary',
        'hover:bg-surface-muted hover:text-fg-primary',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-default'
      )}
    >
      <Plus size={12} aria-hidden="true" />
      <span className="min-w-0 truncate">{label}</span>
    </button>
  </Tooltip>
);

const ChatContextPills = ({ route, surface }: ChatContextPillsProps) => {
  const { open } = useChatDocumentViewer();

  const { anchor, surfaceAnchor, selection, detachedAnchorKey, viewing } =
    useChatAppContextInputs();

  const { current: blockContext, scopes: blockScopes } =
    useChatBlockThreadState();

  const viewerStatus = useChatDocumentViewerStatus();
  const formatSourceLabel = useMcGrawHillSourceLabel(); // mcgraw-hill
  const viewerLabel = viewerStatus ? documentPillLabel(viewerStatus) : null;

  const documentLabel =
    viewerLabel === null ? null : formatSourceLabel(viewerLabel);

  // The lecture on the anchor is the one open beside the chat — View
  // Document's session, or a lecture pill opened in place. One pill says so:
  // the document's, with its page, and no X (closing the document is what
  // takes it away). The envelope still names both; the server reads a
  // `viewing` that repeats the anchored lecture as the anchor alone.
  const isAnchorOnScreen =
    documentLabel !== null &&
    anchor?.type === 'lecture' &&
    viewing?.kind === 'lecture' &&
    viewing.lectureId === anchor.lectureId;

  const showPageContext = hasPageContext(route, surface) && !isAnchorOnScreen;
  const contextKey = chatContextKey(anchor, route);
  const isDetached = showPageContext && detachedAnchorKey === contextKey;
  // The pill reads the *store's* anchor, never the envelope's: a detached
  // envelope has no anchor, and the ghost still has to say what it would put
  // back.
  const kind: ChatAnchorType | 'page' = anchor ? anchor.type : 'page';

  const label = anchor
    ? (anchor.label ?? ANCHOR_FALLBACK_LABEL[anchor.type])
    : (PAGE_LABEL[route] ?? 'This page');

  const noun = anchor ? ANCHOR_NOUN[anchor.type] : 'page';

  const Icon = anchor
    ? anchor.icon
      ? ANCHOR_ICON_OVERRIDE[anchor.icon]
      : CONTEXT_ICON[anchor.type]
    : Browser;

  const iconName: string = anchor ? (anchor.icon ?? anchor.type) : 'page';

  // In a QBank block the question pill also says what this chat covers
  // (ORA-1285). Only for the question the page shows: a question served into
  // the chat claims the anchor without being the block's question on screen.
  const blockScope =
    blockContext &&
    anchor?.type === 'question' &&
    isSameChatAnchor(anchor, surfaceAnchor)
      ? blockScopes[blockContext.blockId]
      : 'ineligible';

  const removeContext = () => {
    setDetachedChatContextKey(contextKey);
    capture('companion_context_removed', { kind, route });
  };

  if (!showPageContext && !selection && !documentLabel) return null;

  return (
    <div
      role="group"
      aria-label="What Ora can see"
      data-testid="chat-context-pills"
      className="flex flex-wrap items-center gap-1.5 px-1 pt-1 max-mobile:w-full"
    >
      {showPageContext ? (
        isDetached ? (
          <GhostPill
            label={label}
            noun={noun}
            onRestore={() => {
              setDetachedChatContextKey(null);
              capture('companion_context_restored', { kind, route });
            }}
          />
        ) : blockContext && blockScope !== 'ineligible' ? (
          <ChatBlockScopePill
            context={blockContext}
            isBlock={blockScope === 'block'}
            onRemove={removeContext}
          />
        ) : (
          <ContextPill
            label={label}
            icon={Icon}
            iconName={iconName}
            testId="chat-context-pill"
            onActivate={
              anchor?.type === 'lecture'
                ? () => open({ kind: 'lecture', lectureId: anchor.lectureId })
                : undefined
            }
            onRemove={removeContext}
          />
        )
      ) : null}
      {documentLabel ? (
        // Closing the viewer takes this pill away, so it has no X of its own.
        <ContextPill
          label={documentLabel}
          icon={FileText}
          testId="chat-context-pill-document"
        />
      ) : null}
      {selection ? (
        <ContextPill
          label={selectionPillLabel(selection)}
          icon={Quotes}
          testId="chat-context-pill-selection"
          removeLabel="Remove highlighted text from this chat"
          onRemove={() => {
            clearHighlightedChatSelection();
            capture('companion_context_removed', { kind: 'selection', route });
          }}
        />
      ) : null}
    </div>
  );
};

export default ChatContextPills;
